import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const POLL_INTERVAL = 60 * 1000; // 1分ごと

export type NotificationType =
  | "course_unlocked"
  | "new_video"
  | "announcement"
  | "system";

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string | null;
  link_url: string | null;
  read_at: string | null;
  created_at: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  unread_count: number;
  total: number;
}

export interface UnreadCountResponse {
  unread_count: number;
}

interface UseNotificationsParams {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
}

async function getAuthToken(): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("認証セッションが見つかりません");
  return token;
}

/** 通知一覧・未読数取得・既読化をまとめたhook */
export function useNotifications({
  limit = 20,
  offset = 0,
  unread_only = false,
}: UseNotificationsParams = {}) {
  const queryClient = useQueryClient();

  const notificationsQuery = useQuery({
    queryKey: ["notifications", "list", { limit, offset, unread_only }],
    queryFn: async (): Promise<NotificationsResponse> => {
      const token = await getAuthToken();
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        unread_only: String(unread_only),
      });
      const response = await fetch(
        `${apiBaseUrl}/api/v1/notifications?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("通知の取得に失敗しました");
      return response.json() as Promise<NotificationsResponse>;
    },
    staleTime: 30 * 1000,
  });

  const markReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const token = await getAuthToken();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/notifications/${notificationId}/mark-read`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok && response.status !== 204)
        throw new Error("既読化に失敗しました");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      const token = await getAuthToken();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/notifications/mark-all-read`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!response.ok) throw new Error("全件既読化に失敗しました");
      return response.json() as Promise<{ marked_count: number }>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });

  return {
    notifications: notificationsQuery.data?.notifications ?? [],
    unreadCount: notificationsQuery.data?.unread_count ?? 0,
    total: notificationsQuery.data?.total ?? 0,
    isLoading: notificationsQuery.isLoading,
    error: notificationsQuery.error,
    refetch: notificationsQuery.refetch,
    markRead: markReadMutation.mutate,
    markReadAsync: markReadMutation.mutateAsync,
    markAllRead: markAllReadMutation.mutate,
    markAllReadAsync: markAllReadMutation.mutateAsync,
    isMarkingRead: markReadMutation.isPending,
    isMarkingAllRead: markAllReadMutation.isPending,
  };
}

/** 未読数だけポーリングする軽量hook（Bellアイコン用） */
export function useUnreadCount() {
  const { data } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async (): Promise<UnreadCountResponse> => {
      const token = await getAuthToken();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/notifications/unread-count`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error("未読数の取得に失敗しました");
      return response.json() as Promise<UnreadCountResponse>;
    },
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL / 2,
    // セッションがない場合はクエリを実行しない
    retry: false,
  });

  return { unreadCount: data?.unread_count ?? 0 };
}
